import type { Breed } from 'shared/@generated/models';
import {
  PetCostStage,
  defaultPetCostProfile,
  petCostProfileFromBreed,
  stageForYear,
} from 'shared/library/pet-costs';

export type DogSize = 'small' | 'medium' | 'large' | 'giant';
export type DogYearsStage = PetCostStage;

export interface DogYearsProfile {
  weightKg: number;
  lifespanYears: number;
}

// Human age for each whole dog year (index = dog age), per size class. Ages 0–16 follow the
// size-based chart veterinarians commonly use (attributed to the AVMA); later years continue its last step.
const humanAgeBySize: Record<DogSize, number[]> = {
  small: [0, 15, 24, 28, 32, 36, 40, 44, 48, 52, 56, 60, 64, 68, 72, 76, 80, 84, 88, 92, 96],
  medium: [0, 15, 24, 28, 32, 36, 42, 47, 51, 56, 60, 65, 69, 74, 78, 83, 87, 91, 95, 99, 103],
  large: [0, 15, 24, 28, 32, 36, 45, 50, 55, 61, 66, 72, 77, 82, 88, 93, 99, 105, 111, 117, 123],
  giant: [
    0, 12, 22, 31, 38, 45, 49, 56, 64, 71, 79, 86, 93, 100, 107, 114, 121, 128, 135, 142, 149,
  ],
};

export const DOG_YEARS_MAX_AGE = 20;

/** Ages the age picker can choose: every month from 2 to 11 months, then every whole year. */
export const DOG_YEARS_AGES = [
  ...Array.from({ length: 10 }, (_, index) => (index + 2) / 12),
  ...Array.from({ length: DOG_YEARS_MAX_AGE }, (_, index) => index + 1),
];

export function dogSize(weightKg: number): DogSize {
  if (weightKg < 10) return 'small';
  if (weightKg < 23) return 'medium';
  if (weightKg < 45) return 'large';

  return 'giant';
}

export function dogYearsProfile(breed: Breed | null): DogYearsProfile {
  const { weightKg, lifespanYears } = breed
    ? petCostProfileFromBreed('dog', breed)
    : defaultPetCostProfile('dog');

  return { weightKg, lifespanYears };
}

/** Human age for a dog age in years, interpolated between whole years and rounded. */
export function humanAge(profile: DogYearsProfile, dogAge: number) {
  const row = humanAgeBySize[dogSize(profile.weightKg)];
  const age = Math.min(DOG_YEARS_MAX_AGE, Math.max(0, dogAge));
  const whole = Math.min(Math.floor(age), DOG_YEARS_MAX_AGE - 1);

  return Math.round(row[whole]! + (row[whole + 1]! - row[whole]!) * (age - whole));
}

/** Birthdays shown in the chart: the expected lifespan plus two years, at least ten. */
export function dogYearsBirthdays(profile: DogYearsProfile) {
  const count = Math.min(DOG_YEARS_MAX_AGE, Math.max(10, profile.lifespanYears + 2));

  return Array.from({ length: count }, (_, index) => index + 1);
}

/** Life stage at a birthday, using the same split as the cost calculator. */
export function dogYearsStage(profile: DogYearsProfile, birthday: number): DogYearsStage {
  return stageForYear(birthday, profile.lifespanYears);
}
